import { createSlice } from '@reduxjs/toolkit';
import { getQueryString } from '../../../../domain/url-provider';
import { getJSONFromHtml } from '../../../../domain/common';
import { SerializedClientProduct } from '../../my-products/interfaces';
import { ICountry } from '../../../../domain/addresses/interfaces';
import { getCountriesAndStates, getCountryById, getStateById } from '../../../../domain/addresses/addresses-provider';
import { IFormControl } from '../../../../ui/forms/interfaces';
import { createControl } from '../../../../domain/validation/validation';
import { default as cloneDeep } from 'lodash/cloneDeep';
import {
    isBillingStateRequired,
    getTaxIdMaxLength,
    getTaxIdMinLength,
    isTaxIdRequired, isTaxIdSelectedStatusRequired
} from '../domain/validation-helpers';
import { TaxIdStatusEnum } from '../../../shopping-cart/cart-bridger-pay/enums/tax-id-status-enum';
import seeAllBenefitsPopup from '../../my-products/components/subscriptions/see-all-benefits/see-all-benefits-popup';

interface IState{
    orderID: number;
    subscription: SerializedClientProduct|null;
    countries: ICountry[];
    billToNameControl: IFormControl;
    addressLine1Control: IFormControl;
    addressLine2Control: IFormControl;
    countryIdControl: IFormControl;
    stateIdControl: IFormControl;
    cityControl: IFormControl;
    zipCodeControl: IFormControl;
    serverError: string;
    isOk: boolean;

    // tax id ------------------
    taxIdPopupShown: boolean;
    taxIdControl: IFormControl;
    taxIdSelectedStatusControl: IFormControl;

    // https://wavesaudio.atlassian.net/browse/MRKT-25620
    seeAllBenefitsPopup: SerializedClientProduct|null;

    creditsUsagePolicyPopup: SerializedClientProduct|null;
}

// https://dev13.waves.com/account/update-subscription-billing-address?orderid=8383
const data = getJSONFromHtml(document.getElementById('subscription-data'));
const subscription = (data?.subscription as SerializedClientProduct) ?? null;

const countries = getCountriesAndStates();
const country = getCountryById(countries, subscription?.cmsCountryID ?? 0);
const state = getStateById(country, subscription?.cmsStateID ?? 0);

/**
 * initial state
 */
const initialState: IState = {
    orderID: Number(getQueryString()['orderid']) || 0,
    subscription,
    countries,
    serverError: '',
    isOk: false,

    billToNameControl: createControl('billToNameControl', subscription?.billingName || '', {
        required: true,
        maxlength: 100,
    }),

    addressLine1Control: createControl('addressLine1Control', subscription?.billingAddress || '', {
        required: true,
        maxlength: 100,
    }),
    addressLine2Control: createControl('addressLine2Control', subscription?.billingAddress2 || '', {
        maxlength: 100,
    }),

    countryIdControl: createControl(
        'countryIdControl',
        Number(country?.countryID) || 0,
        {
            required: true,
        }
    ),

    stateIdControl: createControl(
        'stateIdControl',
        Number(state?.stateID) || 0,
        {
            /**
             * the state is required when selected country has states
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isBillingStateRequired(control);
            }
        }
    ),

    cityControl: createControl('cityControl', subscription?.billingCity || '', {
        required: true,
        maxlength: 100,
    }),
    zipCodeControl: createControl('zipCodeControl', subscription?.billingZipCode || '', {
        maxlength: 10,
    }),

    taxIdPopupShown: false,

    taxIdControl: createControl(
        'taxIdControl',
        subscription?.taxID || '',
        {
            /**
             * the 'taxIdSelectedStatusControl' is required when selected country has property isTaxID === true
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isTaxIdRequired(control);
            },

            minlength: () => {
                return getTaxIdMinLength();
            },

            maxlength: () => {
                return getTaxIdMaxLength();
            },
        }
    ),

    /**
     * Tax id is passed to the server in any case, but it's optional for all countries except a country with
     * CMS_Country.IsTaxID = 1 (like Australia, New Zealand, etc.)
     * taxIdSelectedStatusControl represents tax id radio buttons (yes / no . undefined).
     */
    taxIdSelectedStatusControl: createControl(
        'taxIdSelectedStatus',
        subscription?.taxIDSelectedStatus ?? TaxIdStatusEnum.NO,
        {
            /**
             * the 'taxIdSelectedStatusControl' is required when selected country has property isTaxID === true
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isTaxIdSelectedStatusRequired(control);
            }
        }
    ),

    // https://wavesaudio.atlassian.net/browse/MRKT-25620
    seeAllBenefitsPopup: null,

    creditsUsagePolicyPopup: null,
};

const rootSlice = createSlice({
    name: 'root',
    initialState,
    reducers: {

        main(state, action) {
            return {
                ...state,
                ...action.payload,
            }
        },

        setControl(state, action) {

            const copy = cloneDeep(action.payload.control);

            return {
                ...state,
                [action.payload.controlName]: copy,
            };
        },
    }
});

export default rootSlice;